import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { NamedSquad } from '../lib/squads';
import { DataTable, type DataTableColumn } from './DataTable';
import { Button } from './ui/Button';

// The account's named squads as a data table (#6780): the name, then the repo count
// with up to three names and "+N more". Presenter — the screen owns the rows and every
// write. A reader gets no actions column at all (`canAdminWrite`, owner ruling G21).

/** How many repo names a row shows before "+N more". */
const NAMES_SHOWN = 3;

export function SquadTable(props: {
  readonly rows: readonly NamedSquad[];
  /** Absent for a reader: the row carries no Edit or Delete. */
  readonly onEdit?: ((squad: NamedSquad) => void) | undefined;
  readonly onDelete?: ((squad: NamedSquad) => void) | undefined;
}): JSX.Element {
  const base: readonly DataTableColumn<NamedSquad>[] = [
    {
      key: 'squad',
      rowHeader: true,
      header: t('squads.col_squad'),
      cell: (s) => s.name,
    },
    {
      key: 'repos',
      header: t('squads.col_repos'),
      cell: (s) => (
        <span class="squad-table__repos">
          <span class="squad-table__count">{s.repos.length}</span>
          <For each={s.repos.slice(0, NAMES_SHOWN)}>{(repo) => <code>{repo}</code>}</For>
          <Show when={s.repos.length > NAMES_SHOWN}>
            <span class="squad-table__more">
              {t('squads.repos_more', { count: s.repos.length - NAMES_SHOWN })}
            </span>
          </Show>
        </span>
      ),
    },
  ];

  const actions = (edit: (s: NamedSquad) => void, remove: (s: NamedSquad) => void) => ({
    key: 'actions',
    header: t('squads.col_actions'),
    srOnlyHeader: true,
    align: 'end' as const,
    cell: (s: NamedSquad) => (
      <div class="data-table__actions">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('squads.edit_label', { name: s.name })}
          onClick={() => edit(s)}
        >
          {t('squads.edit')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('squads.delete_label', { name: s.name })}
          onClick={() => remove(s)}
        >
          {t('squads.delete')}
        </Button>
      </div>
    ),
  });

  const columns = (): readonly DataTableColumn<NamedSquad>[] => {
    const edit = props.onEdit;
    const remove = props.onDelete;
    return edit !== undefined && remove !== undefined ? [...base, actions(edit, remove)] : base;
  };

  return (
    <DataTable
      label={t('squads.table_label')}
      columns={columns()}
      rows={props.rows}
      rowKey={(s) => s.key}
    />
  );
}
